import { useState } from 'react'
import { ConfirmDialog } from '../feedback/ConfirmDialog.js'

/**
 * Do not throw away what somebody has typed without asking.
 *
 * Every sheet and dialog on the desk closed on Escape, on a click outside,
 * and on the ✕ — and **took the form with it, silently**. The event editor
 * has seventeen fields; a stray Escape after filling in twelve of them lost
 * all twelve and said nothing. A click outside is easier still: the overlay
 * is most of the screen.
 *
 * Only when there is something to lose. A form nobody has touched closes the
 * moment it is asked to, because a confirmation over an untouched form is
 * the kind of prompt people learn to dismiss without reading — and then
 * dismiss the one that mattered.
 */
export function useCloseGuard(dirty: boolean | undefined, onOpenChange: (open: boolean) => void) {
  const [asking, setAsking] = useState(false)

  /** Pass to the underlying dialog. Intercepts a close, allows an open. */
  const guardedOpenChange = (open: boolean) => {
    if (open) return onOpenChange(true)
    if (dirty) return setAsking(true)
    onOpenChange(false)
  }

  /**
   * Rendered only when there is something to guard.
   *
   * **`ConfirmDialog` is built on `Dialog`, which calls this hook** — so a
   * guard rendered unconditionally would be a dialog rendering a dialog
   * rendering a dialog, for ever. Returning `null` where `dirty` is
   * undefined is what stops it, and every plain dialog on the desk is that
   * case.
   */
  const guard =
    !dirty && !asking ? null : (
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        title="Leave without saving?"
        consequence="What you have typed here is not kept. Nothing else changes."
        confirmLabel="Discard it"
        cancelLabel="Keep editing"
        destructive
        onConfirm={() => {
          setAsking(false)
          onOpenChange(false)
        }}
      />
    )

  return { guardedOpenChange, guard }
}
